import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { addYears, addDays, format } from 'date-fns';
import type { CertificateValidityType } from '@/types/certificate.types';

interface ValidityConfigurationProps {
  validityType: CertificateValidityType;
  validityYears?: number | null;
  customValidityDays?: number | null;
  onValidityTypeChange: (type: CertificateValidityType) => void;
  onValidityYearsChange: (years: number | null) => void;
  onCustomValidityDaysChange: (days: number | null) => void;
}

const ValidityConfiguration: React.FC<ValidityConfigurationProps> = ({
  validityType,
  validityYears,
  customValidityDays,
  onValidityTypeChange,
  onValidityYearsChange,
  onCustomValidityDaysChange,
}) => {
  const { t } = useTranslation();
  const [previewExpiry, setPreviewExpiry] = React.useState<string>('');

  useEffect(() => {
    const issueDate = new Date();
    let expiry: Date | null = null;

    switch (validityType) {
      case 'FOREVER':
        expiry = null;
        break;
      case 'FIXED_YEARS':
        if (validityYears) {
          expiry = addYears(issueDate, validityYears);
        }
        break;
      case 'CUSTOM':
        if (customValidityDays) {
          expiry = addDays(issueDate, customValidityDays);
        }
        break;
    }

    if (expiry) {
      setPreviewExpiry(format(expiry, 'yyyy-MM-dd'));
    } else {
      setPreviewExpiry(t('certificate.validityConfiguration.neverExpires'));
    }
  }, [validityType, validityYears, customValidityDays, t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('certificate.validityConfiguration.title')}</CardTitle>
        <CardDescription>
          {t('certificate.validityConfiguration.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <RadioGroup
          value={validityType}
          onValueChange={(value) =>
            onValidityTypeChange(value as CertificateValidityType)
          }
        >
          <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <RadioGroupItem value="FOREVER" id="forever" />
            <Label htmlFor="forever" className="flex-1 cursor-pointer">
              <div>
                <div className="font-medium">{t('certificate.validityConfiguration.foreverValid')}</div>
                <div className="text-sm text-gray-500">
                  {t('certificate.validityConfiguration.foreverValidDescription')}
                </div>
              </div>
            </Label>
          </div>

          <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <RadioGroupItem value="FIXED_YEARS" id="fixed-years" />
            <Label htmlFor="fixed-years" className="flex-1 cursor-pointer">
              <div>
                <div className="font-medium">{t('certificate.validityConfiguration.fixedYears')}</div>
                <div className="text-sm text-gray-500">
                  {t('certificate.validityConfiguration.fixedYearsDescription')}
                </div>
              </div>
            </Label>
          </div>

          {validityType === 'FIXED_YEARS' && (
            <div className="ml-8 space-y-2">
              <Label htmlFor="validityYears">{t('certificate.validityConfiguration.numberOfYears')}</Label>
              <Input
                id="validityYears"
                type="number"
                min="1"
                max="100"
                value={validityYears || ''}
                onChange={(e) =>
                  onValidityYearsChange(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                placeholder={t('certificate.validityConfiguration.yearsPlaceholder')}
                className="w-32"
              />
            </div>
          )}

          <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <RadioGroupItem value="CUSTOM" id="custom" />
            <Label htmlFor="custom" className="flex-1 cursor-pointer">
              <div>
                <div className="font-medium">{t('certificate.validityConfiguration.customDuration')}</div>
                <div className="text-sm text-gray-500">
                  {t('certificate.validityConfiguration.customDurationDescription')}
                </div>
              </div>
            </Label>
          </div>

          {validityType === 'CUSTOM' && (
            <div className="ml-8 space-y-2">
              <Label htmlFor="customValidityDays">{t('certificate.validityConfiguration.numberOfDays')}</Label>
              <Input
                id="customValidityDays"
                type="number"
                min="1"
                value={customValidityDays || ''}
                onChange={(e) =>
                  onCustomValidityDaysChange(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                placeholder={t('certificate.validityConfiguration.daysPlaceholder')}
                className="w-32"
              />
            </div>
          )}
        </RadioGroup>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-blue-900 mb-1">{t('certificate.validityConfiguration.preview')}</div>
            <div className="text-blue-800">
              <div>{t('certificate.validityConfiguration.issueDate')}: {format(new Date(), 'yyyy-MM-dd')}</div>
              <div>{t('certificate.validityConfiguration.expiryDate')}: {previewExpiry}</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ValidityConfiguration;

